import { useEffect, useRef } from 'react';
import { createFormField, FormFieldBlueprint } from '@backstage/plugin-scaffolder-react/alpha';
import { TextField } from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

// Slugify igual ao slug-input do Directus: NFD, remove acentos, lowercase, hifeniza.
function slugify(input: string): string {
  return String(input)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type Opts = { sourceField?: string };

const Component = (props: FieldExtensionComponentProps<string, Opts>) => {
  const { onChange, rawErrors, required, formData, schema, formContext, uiSchema } =
    props;
  const opts = (uiSchema?.['ui:options'] ?? {}) as Opts;
  const sourceField = opts.sourceField ?? 'name';

  const sourceValue = formContext?.formData?.[sourceField] as string | undefined;
  const userEditedRef = useRef(false);
  const lastAutoRef = useRef<string>('');

  // Auto-gera enquanto o user não editar manualmente o slug.
  useEffect(() => {
    if (userEditedRef.current) return;
    const auto = slugify(sourceValue ?? '');
    if (auto && auto !== formData) {
      lastAutoRef.current = auto;
      onChange(auto);
    } else if (!auto && formData) {
      onChange('');
    }
  }, [sourceValue, formData, onChange]);

  const handleChange = (val: string) => {
    // Se o user escreve algo diferente do que foi auto-gerado por último,
    // marca como "editado manualmente" — não sobrescreve mais.
    if (val !== lastAutoRef.current) {
      userEditedRef.current = true;
    }
    onChange(slugify(val));
  };

  const handleRegen = () => {
    userEditedRef.current = false;
    const auto = slugify(sourceValue ?? '');
    lastAutoRef.current = auto;
    onChange(auto);
  };

  return (
    <TextField
      label={schema.title ?? 'Slug'}
      required={required}
      error={!!rawErrors?.length}
      value={formData ?? ''}
      onChange={e => handleChange(e.target.value)}
      onDoubleClick={handleRegen}
      helperText={
        schema.description ??
        `Gerado automaticamente a partir de "${sourceField}". Edite se quiser; duplo-clique pra regerar.`
      }
      margin="normal"
      fullWidth
      InputProps={{ style: { fontFamily: 'monospace' } }}
    />
  );
};

export const XviaSlugPickerExtension = FormFieldBlueprint.make({
  name: 'xvia-slug-picker',
  params: {
    field: async () => createFormField({
      name: 'XviaSlugPicker',
      component: Component,
    }),
  },
});
